/**
 * useClientIntegrations Hook (Story 5.3)
 *
 * Fetches a client's connected integrations from the API.
 * Returns the integration list and a derived array of connected platform IDs.
 *
 * CRITICAL: The integration API returns objects with a `type` field (NOT `platform`)
 * containing canonical platform identifiers like "google-analytics-4".
 */

import { useQuery } from "@tanstack/react-query";
import { apiGet } from "../lib/apiClient";

interface ClientIntegration {
  id: string;
  type: string;
  accountName: string | null;
  lastSyncAt: string | null;
  syncStatus: string | null;
  tokenStatus: string | null;
  consecutiveFailures: number;
}

export function useClientIntegrations(clientId: string | undefined) {
  const {
    data: integrations = [],
    isLoading,
  } = useQuery<ClientIntegration[]>({
    queryKey: ["client-integrations", clientId],
    queryFn: async () => {
      const res = await apiGet(`/api/clients/${clientId}/integrations`);
      if (!res.ok) return [];
      const json = await res.json();
      return json.data;
    },
    enabled: !!clientId,
  });

  const connectedPlatforms = integrations.map((i) => i.type);

  return { integrations, connectedPlatforms, isLoading };
}
